import { createColumnHelper } from "@tanstack/react-table";
import { PositionFilter } from "@/components/headToHead/PlayerStats/PlayerStatsTable/util";
import TeamCellWithLocation, {
  TeamCellWithLocationProps,
} from "@/components/headToHead/Table/teamCellWithLocation";
import { formatDate, MONTH_DATE } from "@/utils/date";
import type { CellContext } from "@tanstack/react-table";

export type PlayerLogAndStatsDatum = {
  eventId: number;
  timestamp: number;
  team: TeamCellWithLocationProps;
  score: string;
  passAttempts: number | string;
  passTDs: number | string;
  passYards: number | string;
  rushes: number | string;
  rushTDs: number | string;
  rushYards: number | string;
  receptions: number | string;
  recTDs: number | string;
  recYards: number | string;
  fgMade: number | string;
  totalPoints: number | string;
  xpMade: number | string;
  ints: number | string;
  sacks: number | string;
  tacklesForLoss: number | string;
};

const columnHelper = createColumnHelper<PlayerLogAndStatsDatum>();

const renderTeamCell = (
  row: CellContext<PlayerLogAndStatsDatum, TeamCellWithLocationProps>
) => (
  <TeamCellWithLocation
    homeTeam={row.getValue()?.homeTeam}
    opponentTeam={row.getValue()?.opponentTeam}
  />
);

const renderDateCell = (row: CellContext<PlayerLogAndStatsDatum, number>) => {
  const unixSecondsToMilliseconds = row.getValue() * 1000;
  const date = new Date(unixSecondsToMilliseconds);
  return formatDate(date, MONTH_DATE);
};

const gameLogCols = [
  columnHelper.accessor("team", {
    header: "OPP",
    cell: renderTeamCell,
  }),
  columnHelper.accessor("timestamp", {
    header: "Date",
    cell: renderDateCell,
  }),
  columnHelper.accessor("score", {
    header: "Result",
  }),
];

const QBCols = [
  columnHelper.accessor("passAttempts", {
    header: "Pass Attempts",
  }),
  columnHelper.accessor("passTDs", {
    header: "Passing TDs",
  }),
  columnHelper.accessor("passYards", {
    header: "Passing Yards",
  }),
];

const RushCols = [
  columnHelper.accessor("rushes", {
    header: "Rush Attempts",
  }),
  columnHelper.accessor("rushTDs", {
    header: "Rushing TDs",
  }),
  columnHelper.accessor("rushYards", {
    header: "Rushing Yards",
  }),
];

const RecCols = [
  columnHelper.accessor("receptions", {
    header: "Receptions",
  }),
  columnHelper.accessor("recTDs", {
    header: "Receiving TDs",
  }),
  columnHelper.accessor("recYards", {
    header: "Receiving Yards",
  }),
];

const KCols = [
  columnHelper.accessor("fgMade", {
    header: "Field Goals Made",
  }),
  columnHelper.accessor("xpMade", {
    header: "Extra Points Made",
  }),
  columnHelper.accessor("totalPoints", {
    header: "Total Points",
  }),
];

const DSTCols = [
  columnHelper.accessor("ints", {
    header: "Interceptions",
  }),
  columnHelper.accessor("sacks", {
    header: "Sacks",
  }),
  columnHelper.accessor("tacklesForLoss", {
    header: "Tackles for Loss",
  }),
];

export const COLUMN_DEFINITIONS_MAP = {
  [PositionFilter.QUARTERBACK]: [...gameLogCols, ...QBCols, ...RushCols],
  [PositionFilter.RUNNING_BACK]: [...gameLogCols, ...RushCols, ...RecCols],
  [PositionFilter.RECEIVER]: [...gameLogCols, ...RecCols],
  [PositionFilter.TIGHT_END]: [...gameLogCols, ...RecCols],
  [PositionFilter.FLEX]: [...gameLogCols, ...RushCols, ...RecCols],
  [PositionFilter.KICKER]: [...gameLogCols, ...KCols],
  [PositionFilter.DEFENSE]: [...gameLogCols, ...DSTCols],
};
